import React, { useMemo } from 'react';
import { Section, SkeletonItem } from '@atlaskit/side-navigation';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useSiteOptimizerCheck } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/site-optimizer-entitlement-check/index.tsx';
import { getSystemNavigationPaths } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useFeatures } from '../../../controllers/features/index.tsx';
import messages from './messages.tsx';
import { PerformanceMenuItem } from './performance-menu-item/index.tsx';
import { isViewAuditingSelected } from './utils.tsx';

/**
 *  Returns Menu navigation respecting the current user permissions
 *  When updating this file, make sure to also update the following files if necessary:
 * - src/packages/navigation-apps/sidebar/common/src/controllers/use-navigation-path/system
 */
export const TroubleshootingAndSupportSection = () => {
	const { data: features } = useFeatures();
	const { hasSiteOptimizerEntitlement } = useSiteOptimizerCheck();
	const { formatMessage } = useIntl();

	const navigationPath = useMemo(() => getSystemNavigationPaths(), []);

	if (features == null) return <SkeletonItem />;

	return (
		<Section title={formatMessage(messages.troubleshootingGroup)}>
			{features.hasViewAuditing && (
				<MenuItem
					analytics={{ itemId: 'view-auditing' }}
					href={navigationPath.troubleshootingAuditLog}
					selectedOn={isViewAuditingSelected}
				>
					{formatMessage(messages.auditLog)}
				</MenuItem>
			)}
			{features.hasPluginViewer && (
				<MenuItem
					analytics={{ itemId: 'plugin-viewer' }}
					href={navigationPath.troubleshootingPluginViewer}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.pluginViewer)}
				</MenuItem>
			)}
			{features.hasModulesViewer && (
				<MenuItem
					analytics={{ itemId: 'modules-viewer' }}
					href={navigationPath.troubleshootingModulesViewer}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.modulesViewer)}
				</MenuItem>
			)}
			{features.hasSystemInfo && (
				<MenuItem
					analytics={{ itemId: 'system-info' }}
					href={navigationPath.troubleshootingSystemInfo}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.systemInfo)}
				</MenuItem>
			)}
			{features.hasIntegrityChecker && (
				<MenuItem
					analytics={{ itemId: 'integrity-checker' }}
					href={navigationPath.troubleshootingIntegrityChecker}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.integrityChecker)}
				</MenuItem>
			)}
			{features.hasLoggingProfiling && (
				<MenuItem
					analytics={{ itemId: 'logging-profiling' }}
					href={navigationPath.troubleshootingLoggingProfiling}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.loggingProfiling)}
				</MenuItem>
			)}
			{features.hasSchedulerDetails && (
				<MenuItem
					analytics={{ itemId: 'scheduler-details' }}
					href={navigationPath.troubleshootingSchedulerDetails}
					selectedOn={matchUrlPathname()}
				>
					{formatMessage(messages.schedulerDetails)}
				</MenuItem>
			)}
			{hasSiteOptimizerEntitlement && <PerformanceMenuItem />}
		</Section>
	);
};
